export default function ComboboxAutocomplete( comboboxNode, buttonNode, listboxNode ) {
	this.comboboxNode = comboboxNode;
	this.buttonNode = buttonNode;
	this.listboxNode = listboxNode;

	this.comboboxHasVisualFocus = false;
	this.listboxHasVisualFocus = false;

	this.hasHover = false;

	this.isNone = false;
	this.isList = false;
	this.isBoth = false;

	this.allOptions = [];

	this.option = null;
	this.firstOption = null;
	this.lastOption = null;

	this.filteredOptions = [];
	this.filter = '';

	let autocomplete = this.comboboxNode.getAttribute( 'aria-autocomplete' );

	if ( typeof autocomplete === 'string' ) {
		autocomplete = autocomplete.toLowerCase();
		this.isNone = autocomplete === 'none';
		this.isList = autocomplete === 'list';
		this.isBoth = autocomplete === 'both';
	} else {
		this.isNone = true;
	}

	this.init = () => {
		this.comboboxNode.addEventListener( 'keydown', this.onComboboxKeyDown.bind( this ) );
		this.comboboxNode.addEventListener( 'keyup', this.onComboboxKeyUp.bind( this ) );
		this.comboboxNode.addEventListener( 'click', this.onComboboxClick.bind( this ) );
		this.comboboxNode.addEventListener( 'focus', this.onComboboxFocus.bind( this ) );
		this.comboboxNode.addEventListener( 'blur', this.onComboboxBlur.bind( this ) );

		document.body.addEventListener( 'pointerup', this.onBackgroundPointerUp.bind( this ), true );

		this.listboxNode.addEventListener( 'pointerover', this.onListboxPointerover.bind( this ) );
		this.listboxNode.addEventListener( 'pointerout', this.onListboxPointerout.bind( this ) );

		let nodes = this.listboxNode.getElementsByTagName( 'LI' );
		for ( let i = 0; i < nodes.length; i++ ) {
			let node = nodes[i];
			this.allOptions.push( node );

			node.addEventListener( 'click', this.onOptionClick.bind( this ) );
			node.addEventListener( 'pointerover', this.onOptionPointerover.bind( this ) );
			node.addEventListener( 'pointerout', this.onOptionPointerout.bind( this ) );
		}

		this.filterOptions();

		let button = this.comboboxNode.nextElementSibling;
		if ( button && button.tagName === 'BUTTON' ) {
			button.addEventListener( 'click', this.onButtonClick.bind( this ) );
		}
	}


	this.getLowercaseContent = ( node ) => node.textContent.toLowerCase();

	this.isOptionInView = ( option ) => {
		const bounding = option.getBoundingClientRect();
		return (
			bounding.top >= 0 &&
			bounding.left >= 0 &&
			bounding.bottom <= ( window.innerHeight || document.documentElement.clientHeight ) &&
			bounding.right <= ( window.innerWidth || document.documentElement.clientWidth )
		);
	};

	this.setActiveDescendant = ( option ) => {
		if ( option && this.listboxHasVisualFocus ) {
			this.comboboxNode.setAttribute( 'aria-activedescendant', option.id );
			if ( !this.isOptionInView( option ) ) {
				option.scrollIntoView( { behavior: 'smooth', block: 'nearest' } );
			}
		} else {
			this.comboboxNode.setAttribute( 'aria-activedescendant', '' );
		}
	};

	this.setValue = ( value ) => {
		this.filter = value;
		this.comboboxNode.value = this.filter;
		this.comboboxNode.setSelectionRange( this.filter.length, this.filter.length );
		this.filterOptions();
	};

	this.setOption = ( option, flag = false ) => {
		if ( option ) {
			this.option = option;
			this.setCurrentOptionStyle( this.option );
			this.setActiveDescendant( this.option );

			if ( this.isBoth ) {
				this.comboboxNode.value = this.option.textContent;
				if ( flag ) {
					this.comboboxNode.setSelectionRange( this.option.textContent.length, this.option.textContent.length );
				} else {
					this.comboboxNode.setSelectionRange( this.filter.length, this.option.textContent.length );
				}
			}
		}
	};

	this.setVisualFocusCombobox = () => {
		this.listboxNode.classList.remove( 'focus' );
		this.comboboxNode.parentNode.classList.add( 'focus' );
		this.comboboxHasVisualFocus = true;
		this.listboxHasVisualFocus = false;
		this.setActiveDescendant( false );
	};

	this.setVisualFocusListbox = () => {
		this.comboboxNode.parentNode.classList.remove( 'focus' );
		this.comboboxHasVisualFocus = false;
		this.listboxHasVisualFocus = true;
		this.listboxNode.classList.add( 'focus' );
		this.setActiveDescendant( this.option );
	};

	this.removeVisualFocusAll = () => {
		this.comboboxNode.parentNode.classList.remove( 'focus' );
		this.comboboxHasVisualFocus = false;
		this.listboxHasVisualFocus = false;
		this.listboxNode.classList.remove( 'focus' );
		this.option = null;
		this.setActiveDescendant( false );
	};

	this.filterOptions = () => {
		if ( this.isNone ) {
			this.filter = '';
		}

		let option = null;
		let currentOption = this.option;
		let filter = this.filter.toLowerCase();

		this.filteredOptions = [];
		this.listboxNode.innerHTML = '';

		for ( let i = 0; i < this.allOptions.length; i++ ) {
			option = this.allOptions[i];
			if ( filter.length === 0 || this.getLowercaseContent( option ).indexOf( filter ) === 0 ) {
				this.filteredOptions.push( option );
				this.listboxNode.appendChild( option );
			}
		}

		let numItems = this.filteredOptions.length;
		if ( numItems > 0 ) {
			this.firstOption = this.filteredOptions[0];
			this.lastOption = this.filteredOptions[numItems - 1];

			if ( currentOption && this.filteredOptions.indexOf( currentOption ) >= 0 ) {
				option = currentOption;
			} else {
				option = this.firstOption;
			}
		} else {
			this.firstOption = null;
			option = null;
			this.lastOption = null;
		}

		return option;
	};

	this.setCurrentOptionStyle = ( option ) => {
		for ( let i = 0; i < this.filteredOptions.length; i++ ) {
			let opt = this.filteredOptions[i];
			if ( opt === option ) {
				opt.setAttribute( 'aria-selected', 'true' );
				if ( this.listboxNode.scrollTop + this.listboxNode.offsetHeight < opt.offsetTop + opt.offsetHeight ) {
					this.listboxNode.scrollTop = opt.offsetTop + opt.offsetHeight - this.listboxNode.offsetHeight;
				} else if ( this.listboxNode.scrollTop > opt.offsetTop + 2 ) {
					this.listboxNode.scrollTop = opt.offsetTop;
				}
			} else {
				opt.removeAttribute( 'aria-selected' );
			}
		}
	};

	this.getPreviousOption = ( currentOption ) => {
		if ( currentOption !== this.firstOption ) {
			let index = this.filteredOptions.indexOf( currentOption );
			return this.filteredOptions[index - 1];
		}
		return this.lastOption;
	};

	this.getNextOption = ( currentOption ) => {
		if ( currentOption !== this.lastOption ) {
			let index = this.filteredOptions.indexOf( currentOption );
			return this.filteredOptions[index + 1];
		}
		return this.firstOption;
	};

	this.doesOptionHaveFocus = () => {
		return this.comboboxNode.getAttribute( 'aria-activedescendant' ) !== '';
	};

	this.isOpen = () => {
		return this.listboxNode.style.display === 'block';
	};

	this.isClosed = () => {
		return this.listboxNode.style.display !== 'block';
	};

	this.hasOptions = () => {
		return this.filteredOptions.length;
	};

	this.open = () => {
		this.listboxNode.style.display = 'block';
		this.comboboxNode.setAttribute( 'aria-expanded', 'true' );
		this.buttonNode.setAttribute( 'aria-expanded', 'true' );
	};

	this.close = ( force = false ) => {
		if ( force || ( !this.comboboxHasVisualFocus && !this.listboxHasVisualFocus && !this.hasHover ) ) {
			this.setCurrentOptionStyle( false );
			this.listboxNode.style.display = 'none';
			this.comboboxNode.setAttribute( 'aria-expanded', 'false' );
			this.buttonNode.setAttribute( 'aria-expanded', 'false' );
			this.setActiveDescendant( false );
			// this.comboboxNode.parentNode.classList.add( 'focus' );
		}
	};

	this.onComboboxKeyDown = ( event ) => {
		let flag = false,
			altKey = event.altKey;

		if ( event.ctrlKey || event.shiftKey ) {
			return;
		}

		switch ( event.key ) {
			case 'Enter':
				if ( this.listboxHasVisualFocus ) {
					this.setValue( this.option.textContent );
				}
				this.close( true );
				this.setVisualFocusCombobox();
				flag = true;
				break;

			case 'Down':
			case 'ArrowDown':
				if ( this.filteredOptions.length > 0 ) {
					if ( altKey ) {
						this.open();
					} else {
						this.open();
						if ( this.listboxHasVisualFocus || ( this.isBoth && this.filteredOptions.length > 1 ) ) {
							this.setOption( this.getNextOption( this.option ), true );
							this.setVisualFocusListbox();
						} else {
							this.setOption( this.firstOption, true );
							this.setVisualFocusListbox();
						}
					}
				}
				flag = true;
				break;

			case 'Up':
			case 'ArrowUp':
				if ( this.hasOptions() ) {
					if ( this.listboxHasVisualFocus ) {
						this.setOption( this.getPreviousOption( this.option ), true );
					} else {
						this.open();
						if ( !altKey ) {
							this.setOption( this.lastOption, true );
							this.setVisualFocusListbox();
						}
					}
				}
				flag = true;
				break;

			case 'Esc':
			case 'Escape':
				if ( this.isOpen() ) {
					this.close( true );
					this.filter = this.comboboxNode.value;
					this.filterOptions();
					this.setVisualFocusCombobox();
				} else {
					this.setValue( '' );
					this.comboboxNode.value = '';
				}
				this.option = null;
				flag = true;
				break;

			case 'Tab':
				this.close( true );
				if ( this.listboxHasVisualFocus ) {
					if ( this.option ) {
						this.setValue( this.option.textContent );
					}
				}
				break;

			case 'Home':
				this.comboboxNode.setSelectionRange( 0, 0 );
				flag = true;
				break;

			case 'End':
				{
					let length = this.comboboxNode.value.length;
					this.comboboxNode.setSelectionRange( length, length );
					flag = true;
					break;
				}

			default:
				break;
		}

		if ( flag ) {
			event.stopPropagation();
			event.preventDefault();
		}
	};

	this.isPrintableCharacter = ( str ) => {
		return str.length === 1 && str.match( /\S| / );
	};

	this.onComboboxKeyUp = ( event ) => {
		let flag = false,
			option = null,
			char = event.key;

		if ( this.isPrintableCharacter( char ) ) {
			this.filter += char;
		}

		if ( this.comboboxNode.value.length < this.filter.length ) {
			this.filter = this.comboboxNode.value;
			this.option = null;
			this.filterOptions();
		}

		if ( event.key === 'Escape' || event.key === 'Esc' ) {
			return;
		}

		switch ( event.key ) {
			case 'Backspace':
				this.setVisualFocusCombobox();
				this.setCurrentOptionStyle( false );
				this.filter = this.comboboxNode.value;
				this.option = null;
				this.filterOptions();
				flag = true;
				break;

			case 'Left':
			case 'ArrowLeft':
			case 'Right':
			case 'ArrowRight':
			case 'Home':
			case 'End':
				if ( this.isBoth ) {
					this.filter = this.comboboxNode.value;
				} else {
					this.option = null;
					this.setCurrentOptionStyle( false );
				}
				this.setVisualFocusCombobox();
				flag = true;
				break;

			default:
				if ( this.isPrintableCharacter( char ) ) {
					this.setVisualFocusCombobox();
					this.setCurrentOptionStyle( false );
					flag = true;

					if ( this.isList || this.isBoth ) {
						option = this.filterOptions();
						if ( option ) {
							if ( this.isClosed() && this.comboboxNode.value.length ) {
								this.open();
							}

							if (
								this.getLowercaseContent( option ).indexOf(
									this.comboboxNode.value.toLowerCase()
								) === 0
							) {
								this.option = option;
								if ( this.isBoth || this.listboxHasVisualFocus ) {
									this.setCurrentOptionStyle( option );
									if ( this.isBoth ) {
										this.setOption( option );
									}
								}
							} else {
								this.option = null;
								this.setCurrentOptionStyle( false );
							}
						} else {
							this.close();
							this.option = null;
							this.setActiveDescendant( false );
						}
					} else if ( this.comboboxNode.value.length ) {
						this.open();
					}
				}
				break;
		}

		if ( flag ) {
			event.stopPropagation();
			event.preventDefault();
		}
	};

	this.onComboboxClick = () => {
		
		if ( this.isOpen() ) {
			this.close( true );
		} else {
			this.open();
		}
	};

	this.onComboboxFocus = () => {
		
		this.filter = this.comboboxNode.value;
		this.filterOptions();
		this.setVisualFocusCombobox();
		this.option = null;
		this.setCurrentOptionStyle( null );
	};

	this.onComboboxBlur = () => {
		this.removeVisualFocusAll();
	};

	this.onBackgroundPointerUp = ( event ) => {
		if (
			!this.comboboxNode.contains( event.target ) &&
			!this.listboxNode.contains( event.target ) &&
			!this.buttonNode.contains( event.target )
		) {
			
			this.comboboxHasVisualFocus = false;
			this.setCurrentOptionStyle( null );
			this.removeVisualFocusAll();
			setTimeout( this.close.bind( this, true ), 300 );
		}
	};

	this.onButtonClick = () => {
		
		if ( this.isOpen() ) {
			this.close( true );
		} else {
			this.open();
		}
		this.comboboxNode.focus();
		this.setVisualFocusCombobox();
	};

	this.onListboxPointerover = () => {
		this.hasHover = true;
	};

	this.onListboxPointerout = () => {
		this.hasHover = false;
		setTimeout( this.close.bind( this, false ), 300 );
	};

	this.onOptionClick = ( event ) => {
		this.comboboxNode.value = event.target.textContent;
		this.close( true );
	};

	this.onOptionPointerover = () => {
		this.hasHover = true;
		this.open();
	};

	this.onOptionPointerout = () => {
		this.hasHover = false;
		setTimeout( this.close.bind( this, false ), 300 );
	};
}
